import React, { useMemo } from 'react';
import { useLeadIntel } from '../context/LeadIntelContext';
import { useCRM } from '../context/CRMContext';
import SafeIcon from '../common/SafeIcon';

const ComplianceBoardView = () => {
  const { signals, scores } = useLeadIntel();
  const { deals } = useCRM();

  const metrics = useMemo(() => {
    // Filter definitions from JSON
    const complianceSignalTypes = ["RegulatoryNeed", "ComplianceInterest", "DecisionInterest"];
    
    // 1. Totale compliance-signaler (periode)
    const complianceSignals = signals.filter(s => complianceSignalTypes.includes(s.signal_type));
    
    // 2. Virksomheter med forhøyet interesse (Hot)
    const hotLeads = scores.filter(s => s.band === 'Hot');
    
    // 3. Avklarte dialoger (Møte)
    const meetings = deals.filter(d => d.stage_id === 'stage-4' || d.stage_id === 'Møte');
    
    // 4. Pågående prosesser (Open)
    const openDeals = deals.filter(d => d.status === 'Åpen' || d.status === 'Open');
    
    // 5. Avsluttede saker (Won)
    const wonDeals = deals.filter(d => d.status === 'Vunnet' || d.status === 'Won');

    // 6. Overordnet risikobilde Logic
    // "Hvis Hot leads > X og møter < Y → Vis 'Bør følges opp'"
    // Using X=2 and Y=1 to ensure logic triggers in demo data if activity is low relative to interest
    const hotCount = hotLeads.length;
    const meetingCount = meetings.length;
    
    let riskStatus;
    if (hotCount > 2 && meetingCount === 0) {
        riskStatus = { 
            label: 'Bør følges opp', 
            color: 'text-amber-800 bg-amber-50 border-amber-200', 
            icon: '⚠️', 
            desc: `Høy interesse (${hotCount}), men ingen avklarte møter registrert.` 
        };
    } else {
        riskStatus = { 
            label: 'Under kontroll', 
            color: 'text-emerald-800 bg-emerald-50 border-emerald-200', 
            icon: '✅', 
            desc: 'Aktivitet samsvarer med markedssignaler.' 
        };
    }

    return {
      totalSignals: complianceSignals.length,
      hotLeadsCount: hotCount,
      meetingsCount: meetingCount,
      openDealsCount: openDeals.length,
      wonDealsCount: wonDeals.length,
      riskStatus
    };
  }, [signals, scores, deals]);

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen space-y-8 animate-in fade-in">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-slate-200">
            <SafeIcon icon="👁️" className="w-3 h-3" /> Styrevisning (Read-Only)
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Compliance – Oversikt</h1>
          <p className="text-slate-500 font-medium mt-1">Overordnet statusbilde for styre og ledelse.</p>
        </div>
        <button onClick={() => window.print()} className="p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-500 print:hidden">
          <SafeIcon icon="🖨️" className="w-5 h-5" />
        </button>
      </div>

      {/* Context Widget (Hva betyr dette?) */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 flex gap-4 items-start shadow-sm">
        <div className="p-3 bg-blue-100 text-blue-600 rounded-xl shrink-0">
          <SafeIcon icon="ℹ️" className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-bold text-blue-900 text-sm uppercase tracking-wider mb-2">Hva betyr dette?</h3>
          <p className="text-blue-800 text-sm leading-relaxed max-w-3xl font-medium">
            Dette dashboardet gir styre og ledelse en samlet oversikt over hvordan virksomheten jobber med compliance-relaterte henvendelser, risikoindikatorer og oppfølging – uten innsyn i enkeltpersoner eller operative detaljer.
          </p>
        </div>
      </div>

      {/* Top Level Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Signals */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Totale signaler (Periode)</p>
            <div className="flex items-end gap-3">
              <span className="text-5xl font-black text-slate-900">{metrics.totalSignals}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-50">
            <p className="text-xs text-slate-500 font-medium">Relevante markedssignaler fanget opp</p>
          </div>
        </div>

        {/* Interest */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Virksomheter med forhøyet interesse</p>
            <div className="flex items-end gap-3">
              <span className="text-5xl font-black text-rose-600">{metrics.hotLeadsCount}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-50">
            <p className="text-xs text-slate-500 font-medium">Kvalifisert som "Hot" basert på adferd</p>
          </div>
        </div>

        {/* Risk Status */}
        <div className={`p-8 rounded-[2rem] border shadow-sm flex flex-col justify-center items-center text-center ${metrics.riskStatus.color}`}>
          <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-4">Overordnet Risikobilde</p>
          <div className="flex flex-col items-center gap-2">
            <div className="text-4xl mb-2">
              <SafeIcon icon={metrics.riskStatus.icon} />
            </div>
            <span className="text-2xl font-black">{metrics.riskStatus.label}</span>
            <p className="text-sm font-medium opacity-80 mt-1">{metrics.riskStatus.desc}</p>
          </div>
        </div>
      </div>

      {/* Process Metrics */}
      <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mt-8 mb-4 ml-2">Status Oppfølging</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center text-2xl">
            <SafeIcon icon="🤝" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Avklarte dialoger</p>
            <p className="text-3xl font-black text-slate-900">{metrics.meetingsCount}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl">
            <SafeIcon icon="⏳" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pågående prosesser</p>
            <p className="text-3xl font-black text-slate-900">{metrics.openDealsCount}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-2xl">
            <SafeIcon icon="✅" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Avsluttede saker</p>
            <p className="text-3xl font-black text-slate-900">{metrics.wonDealsCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceBoardView;